"use client";
import { Icon } from "@zapier/zinnia-icons";
import { Button, IconButton, Tooltip } from "@zapier/design-system";
import classNames from "classnames";
import styles from "./TemplateBookmarkButton.module.css";
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import type { TemplateBookmarkActions } from "../../types/bookmark";

// Visual variant of the bookmark button:
// - 'icon': Displays as an icon button with a tooltip
// - 'button': Displays as a full button with text and an icon
type BookmarkButtonVariant = "icon" | "button";

export type TemplateBookmarkButtonProps = {
  /** Unique identifier for the template */
  id: string;

  /** Slug for the template, used for tracking and analytics */
  slug: string;

  /** Whether this template is a Zap template (vs multi-product template) */
  isZapTemplate: boolean;

  /** Visual style of the button. Defaults to "icon" */
  buttonVariant?: BookmarkButtonVariant;

  /** Additional CSS classes to apply to the button */
  className?: string;

  /** Actions for the bookmark button */
  templateBookmarkActions: TemplateBookmarkActions;
};

export const TemplateBookmarkButton = ({
  templateBookmarkActions,
  id,
  slug,
  isZapTemplate,
  buttonVariant = "icon",
  className,
}: TemplateBookmarkButtonProps) => {
  const { isBookmarked, isLoading, onSave, onUnsave } = templateBookmarkActions;

  const label = isBookmarked ? "Unsave template" : "Save template";

  const handleTemplateBookmarkClick = async () => {
    try {
      if (isBookmarked) {
        await onUnsave(id);
      } else {
        await onSave(id);
      }

      emitUserInteractionEvent(
        UserInteractionEventName.templateBookmarkButtonClicked,
        {
          event_action: "click",
          interaction_goal: label,
          option_selected: id,
          custom_tracking_fields: {
            template_slug: slug,
            is_zap_template: `${isZapTemplate}`,
            template_id: id,
          },
        },
      );
    } catch (error) {
      console.error("Failed to update template bookmark", { error, id });
    }
  };

  if (buttonVariant === "button") {
    return (
      <Button
        iconPosition="start"
        onPress={handleTemplateBookmarkClick}
        variant="tertiary"
        data-selected={isBookmarked}
        className={classNames(styles.bookmarkFormButton, className)}
        isDisabled={isLoading}
      >
        <Icon
          name={isBookmarked ? "actionBookmarkFill" : "actionBookmark"}
          size={16}
        />
        {isBookmarked ? "Saved" : "Save"}
      </Button>
    );
  }

  return (
    <Tooltip content={label}>
      <IconButton
        isDisabled={isLoading}
        aria-label={label}
        size="small"
        className={classNames([
          styles.button,
          isBookmarked ? styles.saved : styles.default,
          className,
        ])}
        style={{
          padding: "var(--zds-space-2, 2px)",
        }}
        name={isBookmarked ? "actionBookmarkFill" : "actionBookmark"}
        variant="tertiary"
        onPress={handleTemplateBookmarkClick}
      />
    </Tooltip>
  );
};
